import { CareerSiteFiltersOption } from 'api-clients/monolith';
import { ActiveFiltersV2 } from 'contexts/v2/app-context';
import invariant from 'invariant';
import { useCallback } from 'react';
import { MultiSelectFilterKeys } from 'types/filter-types';

import { FilterKey } from '../../components/filters/dropdown-section/filter-dropdown/types';

interface UseFilterActionsProps {
  activeFiltersV2: ActiveFiltersV2;
  setActiveFiltersV2: React.Dispatch<React.SetStateAction<ActiveFiltersV2>>;
  setPage: (page: number) => void;
  accountSlug: string | undefined;
  brandId: string | undefined;
  updateUrlWithParams: (
    accountSlug: string,
    brandId: string | undefined,
    key: string,
    value: string | string[],
  ) => void;
  setIsFetchingJobs?: (loading: boolean) => void;
}
export function useFilterActions({
  activeFiltersV2,
  setActiveFiltersV2,
  setPage,
  accountSlug,
  brandId,
  updateUrlWithParams,
  setIsFetchingJobs,
}: UseFilterActionsProps) {
  invariant(accountSlug, 'accountSlug must be passed to useFilterActions');

  const isMultiSelectFilter = useCallback(
    (key: keyof ActiveFiltersV2): key is MultiSelectFilterKeys => {
      return (
        key === 'category' ||
        key === 'position' ||
        key === 'locationName' ||
        key === 'country' ||
        key === 'state' ||
        key === 'city' ||
        key === 'jobHours' ||
        key === 'jobType'
      );
    },
    [],
  );

  const getActiveFilterKey = useCallback(
    (key: string): keyof ActiveFiltersV2 => {
      const keyMap: Record<string, keyof ActiveFiltersV2> = {
        category: 'category',
        position: 'position',
        locationName: 'locationName',
        jobType: 'jobType',
        jobHours: 'jobHours',
        country: 'country',
        state: 'state',
        city: 'city',
      };
      return keyMap[key] || (key as keyof ActiveFiltersV2);
    },
    [],
  );

  const handleFilterChange = useCallback(
    (
      type: string,
      option: { key: string; label: string },
      checked?: boolean,
    ) => {
      const activeKey = getActiveFilterKey(type);
      const currentValue = activeFiltersV2[activeKey];

      let newValue: CareerSiteFiltersOption | CareerSiteFiltersOption[];
      if (isMultiSelectFilter(activeKey)) {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        const isChecked = checked !== undefined ? checked : true;

        if (isChecked) {
          const exists = currentArray.some(item => item.key === option.key);
          newValue = exists ? currentArray : [...currentArray, option];
        } else {
          newValue = currentArray.filter(item => item.key !== option.key);
        }
      } else {
        newValue = [];
      }

      setActiveFiltersV2(
        (prev: ActiveFiltersV2): ActiveFiltersV2 => ({
          ...prev,
          [activeKey]: newValue,
        }),
      );

      setPage(1);

      setIsFetchingJobs?.(true);
      const arrayValue = newValue;
      const urlValue = arrayValue.map(opt => opt.key);

      updateUrlWithParams(accountSlug, brandId, activeKey, urlValue);
      updateUrlWithParams(accountSlug, brandId, 'page', '1');
    },
    [
      activeFiltersV2,
      setActiveFiltersV2,
      setPage,
      accountSlug,
      brandId,
      updateUrlWithParams,
      setIsFetchingJobs,
      getActiveFilterKey,
      isMultiSelectFilter,
    ],
  );

  const isOptionChecked = useCallback(
    (
      key: string,
      option: CareerSiteFiltersOption,
      activeFiltersV2: ActiveFiltersV2,
      getActiveFilterKey: (key: string) => keyof ActiveFiltersV2,
    ): boolean => {
      const activeKey = getActiveFilterKey(key);
      const currentValue = activeFiltersV2[activeKey];

      if (isMultiSelectFilter(activeKey)) {
        if (!currentValue || !Array.isArray(currentValue)) {
          return false;
        }

        const currentArray = currentValue;
        return currentArray.some(item => item.key === option.key);
      }

      if (
        !currentValue ||
        Array.isArray(currentValue) ||
        !('key' in currentValue)
      ) {
        return false;
      }

      return (currentValue as CareerSiteFiltersOption).key === option.key;
    },
    [isMultiSelectFilter],
  );

  const shouldRenderAsDropdown = useCallback((key: FilterKey): boolean => {
    return key === 'category' || key === 'position' || key === 'locationName';
  }, []);

  const shouldExpandSection = useCallback(
    (
      key: string,
      expandedSections: Record<string, boolean>,
      activeFiltersV2: ActiveFiltersV2,
    ) => {
      if (expandedSections[key] !== undefined) {
        return expandedSections[key];
      }

      const activeKey = getActiveFilterKey(key);
      const value = activeFiltersV2[activeKey];
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        !shouldRenderAsDropdown(key as FilterKey)
      );
    },
    [getActiveFilterKey, shouldRenderAsDropdown],
  );

  return {
    handleFilterChange,
    isOptionChecked,
    isMultiSelectFilter,
    shouldRenderAsDropdown,
    getActiveFilterKey,
    shouldExpandSection,
  };
}
